import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Staff / Driver "My Transport" tab.
 *
 * Surfaces every route manifest where the current user is either:
 *   - the assigned driver, OR
 *   - listed as an escort (second adult / safeguarding crew)
 *
 * The backend handles the filtering (see GET /api/manifests — when the
 * caller's role is driver/staff/room_leader it auto-scopes to
 * `driver_id == self OR escort_ids contains self`).
 *
 * Renders enough detail to actually run the route from a phone:
 *   - departure time + route name + start/end points
 *   - school stops with children to collect/drop off
 *   - "Mark picked up" buttons (POST /api/manifests/{id}/confirm-pickup)
 *   - role pill telling the staff member whether they're DRIVING the
 *     route or ESCORTING someone else's route
 */
const ROUTE_TYPE_LABEL = {
  home_to_school: '🌅 Home → School',
  club_to_school: '🌅 Centre → School',
  school_to_club: '🏫 School → Centre',
  school_to_home: '🏫 School → Home',
  club_to_home: '🌆 Centre → Home',
};

export function MyTransportTab({ user }) {
  const [manifests, setManifests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests?date=${selectedDate}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      if (res.ok) {
        const data = await res.json();
        setManifests(Array.isArray(data) ? data : []);
      } else {
        setManifests([]);
      }
    } catch (_err) {
      setManifests([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { load(); }, [load]);

  const confirmPickup = async (manifestId, childId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/confirm-pickup`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            child_id: childId,
            timestamp: new Date().toISOString(),
            location: null,
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Pickup failed (${res.status})`);
      }
      toast.success('Pickup confirmed.');
      load();
    } catch (err) {
      toast.error('Could not confirm pickup: ' + err.message);
    }
  };

  const startRoute = async (manifestId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/start`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Route started.');
      load();
    } catch (err) {
      toast.error('Could not start route: ' + err.message);
    }
  };

  const completeRoute = async (manifestId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/manifests/${manifestId}/complete`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Route completed.');
      load();
    } catch (err) {
      toast.error('Could not complete route: ' + err.message);
    }
  };

  return (
    <div data-testid="my-transport-tab">
      <div className="page-header">
        <h1>🚐 My Transport</h1>
        <p>Routes you're assigned to today as driver or escort.</p>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0, flex: 1, maxWidth: 280 }}>
            <label>Date</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              data-testid="my-transport-date"
            />
          </div>
          <button className="btn btn-secondary" onClick={load} data-testid="my-transport-refresh">
            🔄 Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="loading">Loading…</div></div>
      ) : manifests.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🚐</div>
            <h3>Nothing for you on this date</h3>
            <p style={{ color: 'hsl(var(--muted-foreground))' }}>
              You're not currently assigned as a driver or escort on any route for{' '}
              {new Date(selectedDate).toLocaleDateString()}.
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {manifests.map((m) => {
            const myId = user?._id;
            const isDriver = m.driver_id === myId;
            const isEscort = (m.escort_ids || []).includes(myId);
            const roleOnRoute = isDriver ? 'DRIVING' : (isEscort ? 'ESCORTING' : 'VIEWING');
            return (
              <div
                key={m._id || m.id}
                className="card"
                style={{
                  borderLeft: `4px solid ${
                    m.status === 'completed' ? 'hsl(145 63% 42%)' :
                    m.status === 'in_progress' ? 'hsl(220 90% 60%)' :
                    'hsl(45 92% 50%)'
                  }`,
                }}
                data-testid={`my-transport-manifest-${m._id || m.id}`}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 0.25rem 0' }}>
                      {ROUTE_TYPE_LABEL[m.route_type] || m.route_type}
                    </h3>
                    <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                      {m.departure_time} → {m.target_completion_time}
                      {' · '}{m.start_point} → {m.end_point}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span
                      style={{
                        fontSize: '0.7rem',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '999px',
                        background: isDriver ? 'hsl(220 90% 90%)' : isEscort ? 'hsl(280 70% 92%)' : 'hsl(var(--muted))',
                        color: 'hsl(var(--foreground))',
                        fontWeight: 600,
                      }}
                      data-testid={`my-transport-role-pill-${m._id || m.id}`}
                    >
                      {roleOnRoute}
                    </span>
                    <span style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', borderRadius: '4px', background: 'hsl(var(--muted))' }}>
                      {(m.status || 'pending').replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                </div>

                {/* Driver / escort summary */}
                <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}>
                  <div><strong>Driver:</strong> {m.driver_name || <span style={{ color: 'hsl(var(--muted-foreground))' }}>Unassigned</span>}</div>
                  {(m.escort_details || []).length > 0 && (
                    <div>
                      <strong>Escorts:</strong>{' '}
                      {m.escort_details.map(e => e.full_name).join(', ')}
                    </div>
                  )}
                </div>

                {/* Action buttons */}
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {m.status === 'pending' && (isDriver || isEscort) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => startRoute(m._id || m.id)}
                      data-testid={`my-transport-start-${m._id || m.id}`}
                    >
                      ▶ Start route
                    </button>
                  )}
                  {m.status === 'in_progress' && (isDriver || isEscort) && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => completeRoute(m._id || m.id)}
                      data-testid={`my-transport-complete-${m._id || m.id}`}
                    >
                      ✓ Complete route
                    </button>
                  )}
                </div>

                {/* School stops + children */}
                {(m.school_stops || []).length > 0 && (
                  <details open style={{ marginTop: '0.75rem' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}>
                      School stops & children
                    </summary>
                    <div style={{ marginTop: '0.5rem' }}>
                      {(m.school_stops || []).map((stop, i) => (
                        <div
                          key={i}
                          style={{
                            marginBottom: '0.75rem',
                            padding: '0.75rem',
                            background: 'hsl(var(--muted))',
                            borderRadius: '6px',
                          }}
                        >
                          <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                            {stop.school_name}
                          </div>
                          <div style={{ fontSize: '0.8125rem', color: 'hsl(var(--muted-foreground))', marginBottom: '0.5rem' }}>
                            {stop.school_address} · {stop.drop_off_point || 'Main gate'}
                          </div>
                          {(stop.children || []).length > 0 && (
                            <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem' }}>
                              {stop.children.map(c => (
                                <li key={c.child_id} style={{ marginBottom: '0.4rem' }}>
                                  <span>{c.child_name}</span>
                                  {c.school_class && <span style={{ color: 'hsl(var(--muted-foreground))' }}> ({c.school_class})</span>}
                                  {c.teacher_name && <span style={{ color: 'hsl(var(--muted-foreground))' }}> — Teacher: {c.teacher_name}</span>}
                                  {' '}
                                  {c.collected ? (
                                    <span style={{ color: 'hsl(145 70% 35%)' }}>✓ Collected</span>
                                  ) : (
                                    (isDriver || isEscort) && m.status === 'in_progress' && (
                                      <button
                                        className="btn btn-secondary"
                                        onClick={() => confirmPickup(m._id || m.id, c.child_id)}
                                        style={{ fontSize: '0.75rem', padding: '0.15rem 0.55rem', marginLeft: '0.4rem' }}
                                        data-testid={`my-transport-pickup-${m._id || m.id}-${c.child_id}`}
                                      >
                                        Mark picked up
                                      </button>
                                    )
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
